// 확장자를 적는 이유: 이 파일은 단위 테스트가 앱 번들 밖(node)에서 그대로 읽는다 — node ESM 은 상대 경로의
// 확장자를 추론하지 않는다. webpack 은 확장자가 있어도 같은 파일을 찾는다.
import { isRequestFailure, requestFailed, requestSucceeded } from './policy.js'

/**
 * 네 번째(또는 세 번째) 자리의 값이 **axios config 인가** — 객체일 때만이다.
 *
 * 옛 호출부 여럿이 그 자리에 뜻 없는 `true`/`false` 를 넘겨 왔다(이 헬퍼가 무시하던 인자다).
 * 그것을 axios 에 그대로 주면 요청이 깨지므로, 옵트아웃(`showErrorMessage`·`logoutOn403`·
 * `showLoading`)을 legacy 호출부에서도 줄 수 있게 하면서 옛 값은 계속 무시한다.
 */
export function axiosConfig(value) {
  return value && typeof value === 'object' ? value : undefined
}

/**
 * **legacy 콜백 헬퍼(`mainVue.$axios.*`) — 계약 한 곳.** 의존성 없는 모듈이다(효과는 `index.js` 가 주입 — 그래서
 * `tests/unit/legacyHelpers.test.js` 가 실제 axios 인스턴스 위에서 본다).
 *
 * 한 호출의 계약:
 *
 * - **성공** — 응답을 해석해 성공 콜백이 있으면 한 번 부르고 `{ ok: true, data }` 로 끝난다.
 * - **요청 실패**(응답 4xx·5xx 의 `HttpError`, 무응답·타임아웃의 axios 오류 — 판정은 `policy.isRequestFailure` 한 곳,
 *   실패 로그 요약과 같은 구분) — 사람에게 할 말은 인터셉터가 이미 했다(토스트·403 처리, 옵트아웃 존중). 헬퍼는 요약을
 *   한 번 찍고, 필드 오류 그릇(`errorObject`)이 있으면 필드 오류를 머지하고, **값** `{ ok: false, error }` 로 끝난다.
 *   다시 던지지 않는다 — 받지 않는 호출부(226곳, R-K 보고서 §4)에서 처리되지 않은 거절이 되기 때문이다.
 * - **그 밖의 오류**(성공 콜백이 던진 `TypeError` 등 — 코드 결함) — 그대로 던진다. 값으로 접으면 결함이 조용해진다.
 *
 * 그래서 **기다려 이어 가는 호출부는 `ok` 를 본다**: 「던져지지 않았으니 성공」은 이제 거짓 성공이다. 특히
 * 토스트를 끈 요청(`showErrorMessage:false`)은 인터셉터가 아무 말도 하지 않으므로 `ok` 를 안 보면 조용한 실패가 된다.
 *
 * `add`·`up`·`patch`·`patchNonLoading` 도 같은 계약이다. 종전에는 `HttpError` 만 필드 오류로 머지하고 값 없이 끝냈고
 * 전송 오류는 다시 던져, 같은 실패를 두 갈래(조용한 `undefined` · 처리되지 않은 거절)로 냈다. 머지는 그대로 하고
 * 끝은 하나(값)로 맞춘다.
 *
 * @param {import('axios').AxiosInstance} client 인터셉터가 설치된 인스턴스
 * @param {{
 *   readResponse: function(object): *,
 *   logFailure: function(*): void,
 *   mergeErrors: function(?object, ?object): void,
 * }} effects
 *   - `readResponse` — 성공 응답 → 콜백이 받을 값(`index.js` 의 응답 해석)
 *   - `logFailure` — 요청 실패 요약을 찍는 한 곳(`logHttpFailure`)
 *   - `mergeErrors` — 필드 오류를 폼의 오류 그릇에 넣는다(`Vue.set`)
 */
export function createLegacyHelpers(client, effects) {
  const { readResponse, logFailure, mergeErrors } = effects

  /**
   * 요청 하나를 계약대로 끝낸다. 성공 콜백은 `try` **밖**에서 부른다 — 콜백의 예외가 요청 실패로 둔갑하지 않게.
   *
   * 콜백은 **있을 때만** 부른다 — 순번 인라인 변경(`PATCH /smenu/{cd}/{ord}` 등 5곳)처럼 결과를 쓸 일이 없는 호출부는
   * URL 만 넘기는데, 무조건 부르면 요청은 200 으로 끝나고 나서 `TypeError: fun is not a function` 이 난다(2026-09-18 쓰기
   * 흐름 검증에서 발견). 응답 해석(`readResponse`)은 콜백과 무관하게 언제나 한 번 돈다.
   *
   * @param {function(): Promise<object>} send 요청을 보내는 함수
   * @param {?function} fun 성공 콜백(선택 — 순번 인라인 변경처럼 결과를 안 쓰는 호출부는 넘기지 않는다)
   * @param {?object} errorObject 필드 오류를 머지할 그릇(`add`·`up`·`patch`)
   * @returns {Promise<{ok: true, data: *}|{ok: false, error: *}>}
   */
  async function settle(send, fun, errorObject) {
    let response

    try {
      response = await send()
    } catch (error) {
      if (!isRequestFailure(error)) throw error

      logFailure(error)
      mergeErrors(errorObject, error.messages)

      return requestFailed(error)
    }

    const data = readResponse(response)

    if (typeof fun === 'function') fun(data)

    return requestSucceeded(data)
  }

  return Object.freeze({
    // `params` 는 **두 번째 인자**가 소유한다 — config 에 실린 `params` 가 그것을 덮으면 조회 조건이 사라진다
    search: (url, data, fun, config) =>
      settle(() => client.get(url, Object.assign({}, axiosConfig(config), { params: data })), fun),

    /**
     * @param {string} url
     * @param {?function} fun 성공 콜백
     * @param {?object} config axios config — 이 앱의 옵트아웃을 legacy 호출부에서도 줄 수 있게 한다([axiosConfig])
     */
    get: (url, fun, config) => settle(() => client.get(url, axiosConfig(config)), fun),

    add: (url, data, errorObject, fun) => settle(() => client.post(url, data), fun, errorObject),

    up: (url, data, errorObject, fun) => settle(() => client.put(url, data), fun, errorObject),

    del: (url, fun) => settle(() => client.delete(url), fun),

    patch: (url, data, errorObject, fun) => settle(() => client.patch(url, data), fun, errorObject),

    post: (url, data, fun) => settle(() => client.post(url, data), fun),

    patchNonLoading: (url, data, errorObject, fun) => settle(() => client.patch(url, data), fun, errorObject),

    searchNonLoading: (url, data, fun) => settle(() => client.get(url, { params: data }), fun),
  })
}
